import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import compassLogo from '@/assets/logo-compass.png';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Heart, MessageCircle, Share2, MapPin, Calendar, Users, Plus, X, Send, UserPlus, UserCheck, LogIn, Star, Copy, Hammer, Inbox, Sparkles, Filter, Trophy, Swords, ImagePlus, Loader2, ShieldCheck, PackageOpen } from 'lucide-react';
import { MessageThreadSheet } from '@/components/MessageThreadSheet';
import { MessagesInbox } from '@/components/MessagesInbox';
import { MatchLogSheet } from '@/components/MatchLogSheet';
import { BracketSheet } from '@/components/BracketSheet';
import { useTradeMatches, useIsMatch } from '@/hooks/useTradeMatches';
import { useUnreadCount, useMessagesRealtime } from '@/hooks/useMessages';
import { getAvatarById } from '@/lib/avatar-options';
import { InkDot } from '@/components/InkDot';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/hooks/useCollection';
import { CardImage } from '@/components/CardImage';
import { CommentSection } from '@/components/CommentSection';
import { SignedImage } from '@/components/SignedImage';
import { DeckDetailSheet } from '@/components/DeckDetailSheet';
import {
  useCommunityPosts, useCreateCommunityPost, useToggleCommunityLike,
  useTradeListings, useCreateTradeListing,
  useEvents, useCreateEvent, useToggleRsvp,
} from '@/hooks/useCommunity';
import { usePulls, useCreatePull, useTogglePullLike } from '@/hooks/usePulls';
import { useCommunityComments, useAddCommunityComment } from '@/hooks/useComments';
import { useFollowing, useToggleFollow } from '@/hooks/useFollows';
import { usePublicDecks, useRateDeck, useCopyDeck, useTogglePublish } from '@/hooks/useDeckSharing';
import { useDecks } from '@/hooks/useDecks';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

type SubTab = 'feed' | 'pulls' | 'decks' | 'trades' | 'events';

const TAG_COLORS: Record<string, string> = {
  Pulls: 'bg-primary/20 text-primary',
  Milestone: 'bg-ink-emerald/20 text-ink-emerald',
  Competitive: 'bg-ink-ruby/20 text-ink-ruby',
  Events: 'bg-ink-sapphire/20 text-ink-sapphire',
  Help: 'bg-ink-amethyst/20 text-ink-amethyst',
};

const EVENT_TYPES: Record<string, string> = {
  Casual: 'bg-ink-emerald/20 text-ink-emerald',
  Prerelease: 'bg-primary/20 text-primary',
  Social: 'bg-ink-sapphire/20 text-ink-sapphire',
};

const TAGS = ['Pulls', 'Milestone', 'Competitive', 'Events', 'Help'] as const;

interface CommunityTabProps {
  onRequestAuth?: () => void;
}

export function CommunityTab({ onRequestAuth }: CommunityTabProps) {
  const { user } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>('feed');
  useEffect(() => {
    const onNav = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.tab === 'community' && ['feed', 'pulls', 'decks', 'trades', 'events'].includes(d.subTab)) {
        setSubTab(d.subTab as SubTab);
      }
    };
    window.addEventListener('lorescout:navigate-subtab', onNav);
    return () => window.removeEventListener('lorescout:navigate-subtab', onNav);
  }, []);
  const [expandedComments, setExpandedComments] = useState<string | null>(null);
  const [feedFilter, setFeedFilter] = useState<'all' | 'following'>('all');

  // Feed state
  const { data: posts, isLoading: postsLoading } = useCommunityPosts();
  const createPost = useCreateCommunityPost();
  const toggleLike = useToggleCommunityLike();
  const [showPostForm, setShowPostForm] = useState(false);
  const [postText, setPostText] = useState('');
  const [postTag, setPostTag] = useState<string>('Pulls');
  const [postImageFile, setPostImageFile] = useState<File | null>(null);
  const [postImagePreview, setPostImagePreview] = useState<string | null>(null);
  const [moderating, setModerating] = useState(false);

  // Comments
  const { data: comments, isLoading: commentsLoading } = useCommunityComments(expandedComments);
  const addComment = useAddCommunityComment();

  // Follows
  const { data: followingSet } = useFollowing();
  const toggleFollow = useToggleFollow();

  // Decks
  const { data: publicDecks, isLoading: decksLoading } = usePublicDecks();
  const { data: myDecks } = useDecks();
  const rateDeck = useRateDeck();
  const copyDeck = useCopyDeck();
  const togglePublish = useTogglePublish();
  const [showPublishPicker, setShowPublishPicker] = useState(false);
  const [viewDeck, setViewDeck] = useState<any>(null);

  // Trade state
  const { data: trades, isLoading: tradesLoading } = useTradeListings();
  const { data: collection } = useCollection();
  const createTrade = useCreateTradeListing();
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [tradeCardId, setTradeCardId] = useState<string>('');
  const [tradeWant, setTradeWant] = useState('');
  const [tradeLocation, setTradeLocation] = useState('');
  const [tradeSort, setTradeSort] = useState<'recent' | 'matches' | 'nearby'>('recent');
  const [showInbox, setShowInbox] = useState(false);
  const [activeMessage, setActiveMessage] = useState<{ id: string; name: string; tradeId?: string; prefill?: string } | null>(null);
  const [bracketEvent, setBracketEvent] = useState<{ id: string; name: string; isOwner: boolean } | null>(null);
  const [matchLogEvent, setMatchLogEvent] = useState<string | null>(null);
  const { data: tradeMatches } = useTradeMatches(trades);
  const unreadCount = useUnreadCount();
  useMessagesRealtime();

  // Current user location for nearby sort
  const [myLocation, setMyLocation] = useState<string | null>(null);
  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('location').eq('user_id', user.id).maybeSingle().then(({ data }) => {
      setMyLocation(data?.location || null);
    });
  }, [user]);

  // Event state
  const { data: events, isLoading: eventsLoading } = useEvents();
  const createEvent = useCreateEvent();
  const toggleRsvp = useToggleRsvp();
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState('Casual');
  const [eventStore, setEventStore] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventSpots, setEventSpots] = useState('20');

  const resetPostForm = () => {
    setShowPostForm(false);
    setPostText('');
    setPostImageFile(null);
    setPostImagePreview(null);
  };

  const handlePickImage = (file: File | null) => {
    if (!file) { setPostImageFile(null); setPostImagePreview(null); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error('Image must be under 6MB'); return; }
    setPostImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setPostImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result as string;
      resolve(s.split(',')[1] || '');
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleCreatePost = async () => {
    if (!postText.trim() && !postImageFile) return;
    if (!user) { onRequestAuth?.(); return; }
    try {
      let imageUrl: string | null = null;

      if (postImageFile) {
        setModerating(true);
        // 1) Moderate via edge function
        const { compressImageToBase64 } = await import('@/lib/image-utils');
        const { base64: b64, mimeType: mt } = await compressImageToBase64(postImageFile, { maxDim: 1024, quality: 0.82 });
        const { data: modData, error: modErr } = await supabase.functions.invoke('moderate-image', {
          body: { imageBase64: b64, mimeType: mt },
        });
        setModerating(false);
        if (modErr) { toast.error('Image check failed. Please try again.'); return; }
        if (!modData?.approved) {
          toast.error(modData?.reason || 'Image not allowed. Only Lorcana cards or packs can be uploaded.');
          return;
        }

        // 2) Upload to storage under user folder
        const ext = (postImageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('community-images')
          .upload(path, postImageFile, { contentType: postImageFile.type, upsert: false });
        if (upErr) { toast.error('Upload failed: ' + upErr.message); return; }
        // Bucket is private — store the storage path; the feed signs URLs on display.
        imageUrl = path;
      }

      await createPost.mutateAsync({ text: postText, tag: postTag, imageUrl });
      toast.success('Posted!');
      resetPostForm();
    } catch (err: any) {
      setModerating(false);
      toast.error(err.message);
    }
  };

  const handleCreateTrade = async () => {
    if (!tradeCardId || !tradeWant.trim()) return;
    try {
      await createTrade.mutateAsync({ haveCardId: tradeCardId, wantDescription: tradeWant, location: tradeLocation });
      toast.success('Trade listing created!');
      setShowTradeForm(false); setTradeCardId(''); setTradeWant(''); setTradeLocation('');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleCreateEvent = async () => {
    if (!eventName.trim() || !eventStore.trim() || !eventDate) return;
    try {
      await createEvent.mutateAsync({ name: eventName, event_type: eventType, store_name: eventStore, event_date: new Date(eventDate).toISOString(), location_text: eventLocation, total_spots: parseInt(eventSpots) || 20 });
      toast.success('Event created!');
      setShowEventForm(false); setEventName(''); setEventStore(''); setEventDate('');
    } catch (err: any) { toast.error(err.message); }
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="px-4 pb-20">
      {/* Sub tabs */}
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3">
        {(['feed', 'pulls', 'decks', 'trades', 'events'] as SubTab[]).map(tab => (
          <button key={tab} onClick={() => setSubTab(tab)} className={`flex-1 rounded-lg py-2 text-xs font-medium font-body capitalize transition-colors ${subTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* PULLS */}
      {subTab === 'pulls' && (
        <PullsSection user={user} onRequestAuth={onRequestAuth} />
      )}

      {/* FEED */}
      {subTab === 'feed' && (
        <div className="mt-3 space-y-3">
          {/* Feed filter */}
          {user && (
            <div className="flex gap-1 rounded-lg bg-secondary p-0.5">
              <button onClick={() => setFeedFilter('all')} className={`flex-1 rounded-md py-1.5 text-[10px] font-medium font-body transition-colors ${feedFilter === 'all' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>All Posts</button>
              <button onClick={() => setFeedFilter('following')} className={`flex-1 rounded-md py-1.5 text-[10px] font-medium font-body transition-colors ${feedFilter === 'following' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>Following</button>
            </div>
          )}
          {user ? (
            <button onClick={() => setShowPostForm(true)} className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body hover:border-primary/40 transition-colors">
              <Plus className="h-4 w-4 text-primary" /> Share something with the community...
            </button>
          ) : (
            <div className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body">
              <LogIn className="h-4 w-4 text-primary" /> Sign in to share something...
            </div>
          )}
          {(() => {
            const filteredPosts = feedFilter === 'following' && followingSet
              ? (posts || []).filter(p => followingSet.has(p.user_id) || p.user_id === user?.id)
              : posts;
            return postsLoading ? (
              <div className="py-12 flex justify-center"><img src={compassLogo} alt="Content loading" className="h-10 w-10 animate-spin" style={{ animationDuration: '3s' }} /></div>
            ) : !filteredPosts?.length ? (
              <div className="py-12 text-center text-muted-foreground font-body text-sm">
                {feedFilter === 'following' ? 'No posts from people you follow yet' : 'No posts yet — be the first!'}
              </div>
            ) : filteredPosts.map(post => (
            <div key={post.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
              <div className="flex items-center gap-2">
                {(() => {
                  const avatar = getAvatarById(post.profile_emoji);
                  return avatar
                    ? <img src={avatar.src} alt={avatar.label} className="h-8 w-8 rounded-full object-cover" />
                    : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-sm">{post.profile_emoji || '🧙'}</span>;
                })()}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground font-body">{post.profile_username}</span>
                  <span className="ml-2 text-[10px] text-muted-foreground font-body">{timeAgo(post.created_at)}</span>
                </div>
                {post.user_id !== user?.id && (
                  <button
                    onClick={() => {
                      if (!user) { onRequestAuth?.(); return; }
                      toggleFollow.mutate({ userId: post.user_id, isFollowing: !!followingSet?.has(post.user_id) });
                    }}
                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${followingSet?.has(post.user_id) ? 'bg-secondary text-foreground border border-primary/30' : 'bg-primary/10 text-primary'}`}
                  >
                    {followingSet?.has(post.user_id) ? <><UserCheck className="h-3 w-3" /> Following</> : <><UserPlus className="h-3 w-3" /> Follow</>}
                  </button>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${TAG_COLORS[post.tag] || TAG_COLORS.Pulls}`}>{post.tag}</span>
              </div>
              <p className="mt-2.5 text-sm text-foreground/90 leading-relaxed font-body">{post.text_content}</p>
              {(post as any).image_url && (
                (post as any).image_url.startsWith('http') ? (
                  <img src={(post as any).image_url} alt="Community post" loading="lazy" className="mt-2.5 w-full max-h-96 object-contain rounded-lg border border-border bg-secondary/40" />
                ) : (
                  <SignedImage bucket="community-images" path={(post as any).image_url} alt="Community post" className="mt-2.5 w-full max-h-96 object-contain rounded-lg border border-border bg-secondary/40" />
                )
              )}
              <div className="mt-3 flex gap-4">
                <button onClick={() => {
                  if (!user) { onRequestAuth?.(); return; }
                  toggleLike.mutate({ postId: post.id, liked: !!post.user_liked });
                }} className={`flex items-center gap-1 transition-colors ${post.user_liked ? 'text-ink-ruby' : 'text-muted-foreground hover:text-ink-ruby'}`}>
                  <Heart className={`h-4 w-4 ${post.user_liked ? 'fill-current' : ''}`} /><span className="text-[10px] font-body">{post.like_count || 0}</span>
                </button>
                <button onClick={() => setExpandedComments(expandedComments === post.id ? null : post.id)} className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors">
                  <MessageCircle className="h-4 w-4" /><span className="text-[10px] font-body">Comments</span>
                </button>
                <button className="flex items-center gap-1 text-muted-foreground"><Share2 className="h-4 w-4" /></button>
              </div>
              {expandedComments === post.id && (
                <CommentSection
                  comments={comments || []}
                  isLoading={commentsLoading}
                  onAdd={(text) => addComment.mutate({ postId: post.id, text })}
                  isPending={addComment.isPending}
                />
              )}
            </div>
          ));
          })()}
        </div>
      )}

      {/* DECKS */}
      {subTab === 'decks' && (
        <div className="mt-3 space-y-3">
          {user ? (
            <button onClick={() => setShowPublishPicker(true)} className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body hover:border-primary/40 transition-colors">
              <Hammer className="h-4 w-4 text-primary" /> Share one of your decks...
            </button>
          ) : (
            <div className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body">
              <LogIn className="h-4 w-4 text-primary" /> Sign in to share your decks...
            </div>
          )}
          {decksLoading ? (
            <LoadingSpinner />
          ) : !publicDecks?.length ? (
            <div className="py-12 text-center text-muted-foreground font-body text-sm">No shared decks yet — be the first!</div>
          ) : publicDecks.map(deck => {
            const avatar = getAvatarById(deck.profile_emoji);
            return (
              <div key={deck.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in cursor-pointer" onClick={() => setViewDeck(deck)}>
                <div className="flex items-center gap-2 mb-2">
                  {avatar ? (
                    <img src={avatar.src} alt={avatar.label} className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-sm">{deck.profile_emoji || '🧙'}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground font-body">{deck.profile_username}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <InkDot ink={deck.ink_1 as InkColor} size="sm" />
                    <InkDot ink={deck.ink_2 as InkColor} size="sm" />
                  </div>
                </div>
                <h4 className="font-display text-sm font-semibold text-foreground">{deck.name}</h4>
                {deck.description && <p className="text-xs text-muted-foreground font-body mt-1 line-clamp-2">{deck.description}</p>}
                {deck.preview_cards && deck.preview_cards.length > 0 && (
                  <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
                    {deck.preview_cards.map((pc) => (
                      <div key={pc.card_id} className="relative flex-shrink-0 w-12">
                        <CardImage src={pc.image_url} alt={pc.name} inkColor={pc.ink_color as InkColor} className="w-12 aspect-[2/3] rounded" />
                        {pc.quantity > 1 && (
                          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground">
                            {pc.quantity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground font-body">
                  <span>{deck.total_cards || 0} cards</span>
                  <span className="flex items-center gap-0.5">
                    <Star className="h-3 w-3 text-primary fill-primary" />
                    {deck.avg_rating ? deck.avg_rating.toFixed(1) : '—'} ({deck.rating_count})
                  </span>
                </div>
                <div className="mt-3 flex gap-2" onClick={e => e.stopPropagation()}>
                  {/* Rate */}
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => {
                          if (!user) { onRequestAuth?.(); return; }
                          rateDeck.mutate({ deckId: deck.id, rating: star });
                        }}
                        className="p-0.5"
                        aria-label={`Rate deck ${star} star${star > 1 ? 's' : ''}`}
                      >
                        <Star className={`h-3.5 w-3.5 transition-colors ${(deck.user_rating || 0) >= star ? 'text-primary fill-primary' : 'text-muted-foreground/30'}`} />
                      </button>
                    ))}
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={() => {
                      if (!user) { onRequestAuth?.(); return; }
                      copyDeck.mutate(deck.id, { onSuccess: () => toast.success('Deck copied to your collection!') });
                    }}
                    className="flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-[10px] font-medium text-primary font-body hover:bg-primary/20 transition-colors"
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TRADES */}
      {subTab === 'trades' && (
        <div className="mt-3 space-y-3">
          {/* Action row */}
          <div className="flex items-center gap-2">
            {user ? (
              <button onClick={() => setShowTradeForm(true)} className="flex flex-1 items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body hover:border-primary/40 transition-colors">
                <Plus className="h-4 w-4 text-primary" /> Post a trade listing...
              </button>
            ) : (
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body">
                <LogIn className="h-4 w-4 text-primary" /> Sign in to post trades...
              </div>
            )}
            {user && (
              <button
                onClick={() => setShowInbox(true)}
                aria-label="Messages"
                className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-card hover:border-primary/40 transition-colors"
              >
                <Inbox className="h-4 w-4 text-primary" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-ink-ruby px-1 text-[9px] font-bold text-white">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Match summary */}
          {user && tradeMatches && tradeMatches.length > 0 && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
              <div className="flex items-center gap-1.5 text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold font-body">
                  {tradeMatches.length} match{tradeMatches.length === 1 ? '' : 'es'} for you
                </span>
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground font-body">
                Based on your wishlist and collection
              </p>
            </div>
          )}

          {/* Sort */}
          {trades && trades.length > 0 && (
            <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5">
              {(['recent', 'matches', 'nearby'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setTradeSort(s)}
                  disabled={s === 'matches' && !user}
                  className={`flex-1 rounded-md py-1.5 text-[10px] font-medium font-body capitalize transition-colors disabled:opacity-40 ${tradeSort === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                >
                  {s === 'nearby' ? <span className="inline-flex items-center gap-1"><Filter className="h-3 w-3" />{s}</span> : s}
                </button>
              ))}
            </div>
          )}

          {tradesLoading ? (
            <LoadingSpinner />
          ) : !trades?.length ? (
            <div className="py-12 text-center text-muted-foreground font-body text-sm">No trade listings yet</div>
          ) : (() => {
            const matchSet = new Map((tradeMatches || []).map(m => [m.listing.id, m.listing.match_score]));
            let sorted = [...trades];
            if (tradeSort === 'matches') {
              sorted = sorted
                .filter(t => matchSet.has(t.id))
                .sort((a, b) => (matchSet.get(b.id) || 0) - (matchSet.get(a.id) || 0));
              if (sorted.length === 0) {
                return (
                  <div className="py-12 text-center text-muted-foreground font-body text-sm">
                    No matching trades right now — add cards to your wishlist!
                  </div>
                );
              }
            } else if (tradeSort === 'nearby' && myLocation) {
              const loc = myLocation.toLowerCase().trim();
              sorted.sort((a, b) => {
                const am = a.location_text?.toLowerCase().includes(loc) ? 1 : 0;
                const bm = b.location_text?.toLowerCase().includes(loc) ? 1 : 0;
                return bm - am;
              });
            }
            return sorted.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                currentUserId={user?.id}
                onMessage={(prefill) => {
                  if (!user) { onRequestAuth?.(); return; }
                  setActiveMessage({
                    id: trade.user_id,
                    name: trade.profile_username || 'User',
                    tradeId: trade.id,
                    prefill,
                  });
                }}
              />
            ));
          })()}
        </div>
      )}


      {/* EVENTS */}
      {subTab === 'events' && (
        <div className="mt-3 space-y-3">
          {user ? (
            <button onClick={() => setShowEventForm(true)} className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body hover:border-primary/40 transition-colors">
              <Plus className="h-4 w-4 text-primary" /> Create an event...
            </button>
          ) : (
            <div className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body">
              <LogIn className="h-4 w-4 text-primary" /> Sign in to create events...
            </div>
          )}
          {eventsLoading ? (
            <LoadingSpinner />
          ) : !events?.length ? (
            <div className="py-12 text-center text-muted-foreground font-body text-sm">No upcoming events</div>
          ) : events.map(event => {
            const spotsLeft = (event.total_spots || 20) - (event.rsvp_count || 0);
            return (
              <div key={event.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-sm font-semibold text-foreground">{event.name}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${EVENT_TYPES[event.event_type] || EVENT_TYPES.Casual}`}>{event.event_type}</span>
                </div>
                <p className="text-xs text-muted-foreground font-body">{event.store_name}</p>
                <div className="mt-2 flex items-center gap-4 text-[10px] text-muted-foreground font-body">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  {event.location_text && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{event.location_text}</span>}
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{spotsLeft > 0 ? `${spotsLeft} spots` : 'Full'}</span>
                </div>
                <button
                  onClick={() => {
                    if (!user) { onRequestAuth?.(); return; }
                    toggleRsvp.mutate({ eventId: event.id, rsvped: !!event.user_rsvped });
                  }}
                  disabled={spotsLeft <= 0 && !event.user_rsvped}
                  className={`mt-3 w-full rounded-lg py-2.5 text-xs font-medium font-body transition-colors ${event.user_rsvped ? 'bg-secondary text-foreground border border-primary' : 'bg-primary text-primary-foreground'} disabled:opacity-50`}
                >
                  {event.user_rsvped ? '✓ RSVP\'d — Tap to Cancel' : 'RSVP'}
                </button>

                {/* Tournament tools */}
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => {
                      if (!user) { onRequestAuth?.(); return; }
                      setBracketEvent({ id: event.id, name: event.name, isOwner: event.created_by === user.id });
                    }}
                    className="flex-1 flex items-center justify-center gap-1 rounded-lg border border-border bg-background/40 py-1.5 text-[10px] font-medium text-foreground font-body hover:border-primary/40 transition-colors"
                  >
                    <Trophy className="h-3 w-3 text-primary" />
                    {event.bracket_status === 'completed' ? 'Bracket · Done' : event.bracket_status === 'generated' ? 'View Bracket' : 'Bracket'}
                  </button>
                  {event.user_rsvped && (
                    <button
                      onClick={() => setMatchLogEvent(event.id)}
                      className="flex-1 flex items-center justify-center gap-1 rounded-lg border border-border bg-background/40 py-1.5 text-[10px] font-medium text-foreground font-body hover:border-primary/40 transition-colors"
                    >
                      <Swords className="h-3 w-3 text-primary" />
                      Log Match
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Post Form */}
      {showPostForm && (
        <ModalSheet title="New Post" onClose={resetPostForm}>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {TAGS.map(t => (
              <button key={t} onClick={() => setPostTag(t)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${postTag === t ? TAG_COLORS[t] + ' ring-1 ring-primary' : 'bg-secondary text-secondary-foreground'}`}>{t}</button>
            ))}
          </div>
          <textarea value={postText} onChange={e => setPostText(e.target.value)} placeholder="What's happening in your Lorcana world?" rows={3} className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />

          {/* Image upload */}
          {postImagePreview ? (
            <div className="mt-3 relative">
              <img src={postImagePreview} alt="Upload preview" className="w-full max-h-64 object-contain rounded-lg border border-border bg-secondary" />
              <button onClick={() => handlePickImage(null)} className="absolute top-2 right-2 rounded-full bg-background/80 p-1 backdrop-blur-sm" aria-label="Remove image">
                <X className="h-4 w-4 text-foreground" />
              </button>
            </div>
          ) : (
            <label className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/50 py-3 text-xs font-body text-muted-foreground hover:border-primary/40 transition-colors">
              <ImagePlus className="h-4 w-4 text-primary" />
              Add a photo of a card or pack
              <input type="file" accept="image/*" className="hidden" onChange={e => handlePickImage(e.target.files?.[0] || null)} />
            </label>
          )}
          <p className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground font-body">
            <ShieldCheck className="h-3 w-3 text-primary" />
            Images are auto-checked — only Lorcana cards and packs are allowed.
          </p>

          <button
            onClick={handleCreatePost}
            disabled={(!postText.trim() && !postImageFile) || createPost.isPending || moderating}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
          >
            {moderating ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking image...</> : createPost.isPending ? 'Posting...' : <><Send className="h-4 w-4" /> Post</>}
          </button>
        </ModalSheet>
      )}

      {/* Create Trade Form */}
      {showTradeForm && (
        <ModalSheet title="New Trade Listing" onClose={() => setShowTradeForm(false)}>
          <p className="text-xs text-muted-foreground font-body mb-2">Select a card you have:</p>
          {!collection?.length ? (
            <p className="text-sm text-muted-foreground font-body py-4 text-center">Add cards to your collection first</p>
          ) : (
            <div className="grid grid-cols-5 gap-1.5 max-h-36 overflow-y-auto mb-3">
              {collection.map(item => {
                const card = item.card;
                if (!card) return null;
                return (
                  <button key={item.id} onClick={() => setTradeCardId(card.id)} className={`rounded-lg border p-0.5 transition-colors ${tradeCardId === card.id ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                    <CardImage src={card.image_url} alt={card.name} className="aspect-[2/3] rounded" />
                  </button>
                );
              })}
            </div>
          )}
          <input type="text" value={tradeWant} onChange={e => setTradeWant(e.target.value)} placeholder="What do you want in return?" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary mb-2" />
          <input type="text" value={tradeLocation} onChange={e => setTradeLocation(e.target.value)} placeholder="Your area (optional)" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          <button onClick={handleCreateTrade} disabled={!tradeCardId || !tradeWant.trim() || createTrade.isPending} className="mt-3 w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
            {createTrade.isPending ? 'Creating...' : 'Post Trade'}
          </button>
        </ModalSheet>
      )}

      {/* Create Event Form */}
      {showEventForm && (
        <ModalSheet title="New Event" onClose={() => setShowEventForm(false)}>
          <div className="space-y-2">
            <input type="text" value={eventName} onChange={e => setEventName(e.target.value)} placeholder="Event name" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            <div className="flex gap-1.5">
              {(['Casual', 'Prerelease', 'Social'] as const).map(t => (
                <button key={t} onClick={() => setEventType(t)} className={`flex-1 rounded-lg py-2 text-xs font-medium font-body transition-colors ${eventType === t ? EVENT_TYPES[t] + ' ring-1 ring-primary' : 'bg-secondary text-secondary-foreground'}`}>{t}</button>
              ))}
            </div>
            <input type="text" value={eventStore} onChange={e => setEventStore(e.target.value)} placeholder="Store name" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            <input type="datetime-local" value={eventDate} onChange={e => setEventDate(e.target.value)} className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" />
            <div className="flex gap-2">
              <input type="text" value={eventLocation} onChange={e => setEventLocation(e.target.value)} placeholder="Location (optional)" className="flex-1 rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
              <input type="number" value={eventSpots} onChange={e => setEventSpots(e.target.value)} placeholder="Spots" className="w-20 rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>
          <button onClick={handleCreateEvent} disabled={!eventName.trim() || !eventStore.trim() || !eventDate || createEvent.isPending} className="mt-3 w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
            {createEvent.isPending ? 'Creating...' : 'Create Event'}
          </button>
        </ModalSheet>
      )}

      {/* Publish Deck Picker */}
      {showPublishPicker && (
        <ModalSheet title="Share a Deck" onClose={() => setShowPublishPicker(false)}>
          {!myDecks?.length ? (
            <p className="text-sm text-muted-foreground font-body py-4 text-center">Create a deck in your Collection first</p>
          ) : (
            <div className="space-y-2">
              {myDecks.map(deck => {
                const isPublished = deck.published;
                return (
                  <button
                    key={deck.id}
                    onClick={() => {
                      togglePublish.mutate({ deckId: deck.id, published: !isPublished });
                      if (!isPublished) toast.success('Deck shared!');
                      else toast.success('Deck unpublished');
                      setShowPublishPicker(false);
                    }}
                    className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${isPublished ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  >
                    <div className="flex items-center gap-1">
                      <InkDot ink={deck.ink_1 as InkColor} size="sm" />
                      <InkDot ink={deck.ink_2 as InkColor} size="sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground font-body truncate">{deck.name}</p>
                      <p className="text-[10px] text-muted-foreground font-body">{deck.total_cards} cards</p>
                    </div>
                    <span className={`text-[10px] font-medium font-body ${isPublished ? 'text-primary' : 'text-muted-foreground'}`}>
                      {isPublished ? '✓ Shared' : 'Share'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </ModalSheet>
      )}

      <DeckDetailSheet deck={viewDeck} open={!!viewDeck} onOpenChange={(o) => { if (!o) setViewDeck(null); }} />

      <MessagesInbox open={showInbox} onClose={() => setShowInbox(false)} />
      <MessageThreadSheet
        open={!!activeMessage}
        onClose={() => setActiveMessage(null)}
        partnerId={activeMessage?.id ?? null}
        partnerUsername={activeMessage?.name ?? ''}
        tradeListingId={activeMessage?.tradeId}
        prefill={activeMessage?.prefill}
      />

      {bracketEvent && (
        <BracketSheet
          open={!!bracketEvent}
          onClose={() => setBracketEvent(null)}
          eventId={bracketEvent.id}
          eventName={bracketEvent.name}
          isOwner={bracketEvent.isOwner}
        />
      )}
      <MatchLogSheet
        open={!!matchLogEvent}
        onClose={() => setMatchLogEvent(null)}
        eventId={matchLogEvent}
      />
    </div>
  );
}

function TradeCard({
  trade,
  currentUserId,
  onMessage,
}: {
  trade: import('@/hooks/useCommunity').TradeListing;
  currentUserId: string | undefined;
  onMessage: (prefill?: string) => void;
}) {
  const matchReasons = useIsMatch(trade);
  const isOwn = trade.user_id === currentUserId;
  return (
    <div className={`rounded-xl border p-4 animate-fade-in ${matchReasons ? 'border-primary/50 bg-primary/[0.03]' : 'border-border bg-card'}`}>
      {matchReasons && (
        <div className="mb-2 flex items-center gap-1.5 text-primary">
          <Sparkles className="h-3 w-3" />
          <span className="text-[10px] font-semibold font-body">{matchReasons.join(' · ')}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium text-foreground font-body">{trade.profile_username}</span>
        {trade.location_text && (
          <span className="flex items-center gap-1 ml-auto text-[10px] text-muted-foreground font-body">
            <MapPin className="h-3 w-3" />
            {trade.location_text}
          </span>
        )}
      </div>
      <div className="flex gap-3">
        {trade.card && (
          <CardImage
            src={trade.card.image_url}
            alt={trade.card.name}
            inkColor={trade.card.ink_color as InkColor}
            showGlow
            className="w-16 aspect-[2/3] flex-shrink-0 rounded"
          />
        )}
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-ink-emerald font-medium font-body">HAVE</span>
            {trade.card && <InkDot ink={trade.card.ink_color as InkColor} />}
            <span className="text-xs text-foreground font-body truncate">
              {trade.card?.name}{trade.card?.subtitle ? ` — ${trade.card.subtitle}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-ink-ruby font-medium font-body">WANT</span>
            <span className="text-xs text-foreground font-body">{trade.want_description}</span>
          </div>
        </div>
      </div>
      {!isOwn && (
        <button
          onClick={() => onMessage(`Hi! I'm interested in your ${trade.card?.name} trade.`)}
          className="mt-3 w-full rounded-lg bg-primary py-2 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors"
        >
          Message to Trade
        </button>
      )}
    </div>
  );
}


function PullsSection({ user, onRequestAuth }: { user: any; onRequestAuth?: () => void }) {
  const { data: pulls, isLoading } = usePulls();
  const createPull = useCreatePull();
  const toggleLike = useTogglePullLike();
  const [showForm, setShowForm] = useState(false);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [moderating, setModerating] = useState(false);

  const pickImage = (f: File | null) => {
    if (!f) { setFile(null); setPreview(null); return; }
    if (!f.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error('Image must be under 6MB'); return; }
    setFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result as string);
    r.readAsDataURL(f);
  };

  const reset = () => { setShowForm(false); setCaption(''); setFile(null); setPreview(null); };

  const handleSubmit = async () => {
    if (!file) { toast.error('Add a photo of your pull'); return; }
    if (!user) { onRequestAuth?.(); return; }
    try {
      setModerating(true);
      const { compressImageToBase64 } = await import('@/lib/image-utils');
      const { base64, mimeType } = await compressImageToBase64(file, { maxDim: 1024, quality: 0.82 });
      const { data: modData, error: modErr } = await supabase.functions.invoke('moderate-image', {
        body: { imageBase64: base64, mimeType },
      });
      setModerating(false);
      if (modErr) { toast.error('Image check failed. Please try again.'); return; }
      if (!modData?.approved) {
        toast.error(modData?.reason || 'Image not allowed. Only Lorcana cards or packs can be uploaded.');
        return;
      }
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${user.id}/pulls/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('community-images')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) { toast.error('Upload failed: ' + upErr.message); return; }
      await createPull.mutateAsync({ imageUrl: path, caption });
      toast.success('Pull shared!');
      reset();
    } catch (err: any) {
      setModerating(false);
      toast.error(err.message);
    }
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  return (
    <div className="mt-3 space-y-3">
      {user ? (
        <button onClick={() => setShowForm(true)} className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body hover:border-primary/40 transition-colors">
          <PackageOpen className="h-4 w-4 text-primary" /> Share your latest pulls...
        </button>
      ) : (
        <div className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border bg-card/50 p-3 text-sm text-muted-foreground font-body">
          <LogIn className="h-4 w-4 text-primary" /> Sign in to share your pulls...
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : !pulls?.length ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">No pulls yet — be the first to share!</div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {pulls.map(pull => {
            const avatar = getAvatarById(pull.profile_emoji);
            return (
              <div key={pull.id} className="rounded-xl border border-border bg-card overflow-hidden animate-fade-in">
                <SignedImage
                  bucket="community-images"
                  path={pull.image_url}
                  alt={pull.caption || 'Card pull'}
                  className="w-full aspect-square object-cover bg-secondary/40"
                />
                <div className="p-2">
                  <div className="flex items-center gap-1.5">
                    {avatar ? (
                      <img src={avatar.src} alt={avatar.label} className="h-5 w-5 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[10px]">{pull.profile_emoji || '🧙'}</span>
                    )}
                    <span className="text-[11px] font-medium text-foreground font-body truncate flex-1">{pull.profile_username}</span>
                    <span className="text-[9px] text-muted-foreground font-body">{timeAgo(pull.created_at)}</span>
                  </div>
                  {pull.caption && <p className="mt-1 text-[11px] text-foreground/90 font-body line-clamp-2">{pull.caption}</p>}
                  <button
                    onClick={() => {
                      if (!user) { onRequestAuth?.(); return; }
                      toggleLike.mutate({ pullId: pull.id, liked: !!pull.user_liked });
                    }}
                    className={`mt-1.5 flex items-center gap-1 transition-colors ${pull.user_liked ? 'text-ink-ruby' : 'text-muted-foreground hover:text-ink-ruby'}`}
                  >
                    <Heart className={`h-3.5 w-3.5 ${pull.user_liked ? 'fill-current' : ''}`} />
                    <span className="text-[10px] font-body">{pull.like_count || 0}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ModalSheet title="Share a Pull" onClose={reset}>
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Pull preview" className="w-full max-h-72 object-contain rounded-lg border border-border bg-secondary" />
              <button onClick={() => pickImage(null)} className="absolute top-2 right-2 rounded-full bg-background/80 p-1 backdrop-blur-sm" aria-label="Remove image">
                <X className="h-4 w-4 text-foreground" />
              </button>
            </div>
          ) : (
            <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/50 py-6 text-xs font-body text-muted-foreground hover:border-primary/40 transition-colors">
              <ImagePlus className="h-4 w-4 text-primary" />
              Add a photo of your pull
              <input type="file" accept="image/*" className="hidden" onChange={e => pickImage(e.target.files?.[0] || null)} />
            </label>
          )}
          <p className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground font-body">
            <ShieldCheck className="h-3 w-3 text-primary" />
            Images are auto-checked — only Lorcana cards and packs are allowed.
          </p>
          <textarea
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="What did you pull? (optional)"
            rows={2}
            className="mt-3 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={!file || createPull.isPending || moderating}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
          >
            {moderating ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking image...</> : createPull.isPending ? 'Posting...' : <><Send className="h-4 w-4" /> Share Pull</>}
          </button>
        </ModalSheet>
      )}
    </div>
  );
}

function ModalSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[80vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
